import React, { useEffect } from 'react'
import clsx from 'clsx'
import { useFocusTrap } from '@/hooks/useFocusTrap'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  subtitle?: string
  children: React.ReactNode
  footer?: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  closeButton?: boolean
  backdrop?: 'dark' | 'light' | 'blur'
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  closeButton = true,
  backdrop = 'blur',
}) => {
  // Focus trap for accessibility (WCAG 2.1)
  const modalRef = useFocusTrap<HTMLDivElement>(isOpen, {
    onEscape: onClose,
    restoreFocus: true,
    initialFocus: true,
  })

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
  }

  const backdropClasses = {
    dark: 'bg-black/60',
    light: 'bg-white/20 backdrop-blur-sm',
    blur: 'bg-black/40 backdrop-blur-md',
  }

  return (
    <div className="fixed inset-0" style={{ zIndex: 'var(--z-modal-backdrop)' }}>
      {/* Backdrop */}
      <div
        className={clsx('absolute inset-0 transition-opacity', backdropClasses[backdrop])}
        onClick={onClose}
        role="presentation"
      />

      {/* Modal */}
      <div className="relative flex items-center justify-center min-h-screen p-4" style={{ zIndex: 'var(--z-modal)' }}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div
          ref={modalRef}
          className={clsx(
            'w-full bg-[var(--bg-card)] rounded-[var(--radius-xl)]',
            'shadow-[var(--shadow-lg)] border border-[var(--border)]',
            'transform transition-all',
            sizeClasses[size]
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? "modal-title" : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {(title || closeButton) && (
            <div className="flex items-start justify-between p-6 border-b border-[var(--border)]">
              {title && (
                <div>
                  <h2
                    id="modal-title"
                    className="text-lg font-semibold text-[var(--text-primary)]"
                  >
                    {title}
                  </h2>
                  {subtitle && (
                    <p className="mt-1 text-sm text-[var(--text-muted)]">{subtitle}</p>
                  )}
                </div>
              )}
              {closeButton && (
                <button
                  onClick={onClose}
                  className="ml-auto text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                  aria-label="Close modal"
                >
                  <span className="text-xl">✕</span>
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div className="p-6 overflow-y-auto max-h-[calc(100vh-300px)]">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="p-6 border-t border-[var(--border)] bg-[var(--bg-canvas)] rounded-b-[var(--radius-xl)]">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Modal